import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Field } from "@/components/ui-kit";
import { useFilePreview } from "@/context/FilePreviewContext";
import SignatureOnFile from "@/components/SignatureOnFile";
import { STATUS_META } from "@/pages/Compliance";
import { toast } from "sonner";
import {
  ArrowLeft, Archive, Printer, GraduationCap, Upload, CheckCircle2,
  AlertTriangle, Trash2, FilePlus2,
} from "lucide-react";

const REQUEST_PRESETS = [
  "Drug & Alcohol Screening",
  "Initial STS Driver Training",
  "Refresher Training",
  "Re-upload a document",
  "DOT physical / medical card",
  "Other",
];

/**
 * Driver File workspace: one page per driver with every requirement, request, note,
 * license read, and signature in one place, backed by one round-trip GET /drivers/{id}/file.
 * Admin area only (agency_admin, dispatcher) - the backend 403s a driver here.
 */
export default function DriverFile() {
  const { id } = useParams();
  const { openPreview } = useFilePreview();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/drivers/${id}/file`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    api.get(`/drivers/${id}/onboarding`).then((r) => setOnboarding(r.data)).catch(() => setOnboarding(null));
  }, [id]);

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading…</div>;
  if (!data) {
    return (
      <div className="text-sm text-slate-500 p-4">
        Driver not found. <Link to="/app/compliance" className="text-[#005E82] hover:underline">Back to Compliance</Link>
      </div>
    );
  }

  const { driver, documents, training_status, license_prefill, requests, notes } = data;
  const generalNotes = notes.filter((n) => !n.section || n.section === "general");
  const docTypeOptions = documents.map((d) => ({ key: d.doc_type, label: d.label }));

  const printApplication = () => {
    const appSection = documents.find((d) => d.doc_type === "work_application");
    const latest = appSection?.versions?.[0];
    if (!latest) return toast.error("No application on file for this driver yet.");
    openPreview({
      kind: "document",
      id: latest.id,
      meta: {
        label: "Application for employment",
        driver_name: driver.name,
        doc_type: "work_application",
        status: appSection.status,
        expires_at: latest.expires_at,
        uploaded_at: latest.uploaded_at,
        original_filename: latest.original_filename,
        content_type: latest.content_type,
        driver_id: id,
        canEdit: false,
        notes: latest.notes,
      },
    });
  };

  const openZip = () => {
    const token = localStorage.getItem("nemt_token");
    window.open(`${API_BASE}/drivers/${id}/documents/export.zip?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  return (
    <div data-testid="driver-file-page">
      <Link to="/app/compliance" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0A192F] mb-3" data-testid="back-to-compliance">
        <ArrowLeft size={13} /> Back to Compliance
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Driver File</div>
          <h1 className="font-display text-2xl font-bold text-[#0A192F] mt-1">{driver.name}</h1>
          <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-2 flex-wrap">
            {driver.phone && <span>{driver.phone}</span>}
            {driver.email && <span>{driver.email}</span>}
            <span className="inline-flex items-center rounded-full px-2 py-0.5 border border-slate-200 text-slate-600 text-[10px] font-medium capitalize">
              {(driver.status || "active").replace("_", " ")}
            </span>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 border border-slate-200 text-slate-600 text-[10px] font-medium">Driver</span>
            {onboarding && <OnboardingPill onboarding={onboarding} />}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/app/drivers/${id}/training`}
            className="text-xs px-3 rounded-md border border-slate-200 min-h-[44px] inline-flex items-center gap-1.5"
            data-testid="file-training-link"
          >
            <GraduationCap size={13} /> Training
          </Link>
          <button onClick={openZip} className="text-xs px-3 rounded-md border border-slate-200 min-h-[44px] inline-flex items-center gap-1.5" data-testid="file-zip-btn">
            <Archive size={13} /> Documents zip
          </button>
          <button onClick={printApplication} className="btn-primary text-xs px-3 min-h-[44px] inline-flex items-center gap-1.5" data-testid="file-print-app-btn">
            <Printer size={13} /> Print application
          </button>
        </div>
      </div>

      {training_status && (
        <div
          className={`mb-5 rounded-md border-l-4 p-4 ${training_status.ok ? "bg-emerald-50 border-emerald-500" : "bg-rose-50 border-rose-500"}`}
          data-testid="file-gate-banner"
        >
          <div className={`flex items-center gap-2 font-semibold text-sm ${training_status.ok ? "text-emerald-700" : "text-rose-700"}`}>
            {training_status.ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {training_status.summary}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {documents.map((d) => (
            <DocSection
              key={d.doc_type}
              driverId={id}
              docType={d.doc_type}
              label={d.label}
              status={d.status}
              versions={d.versions}
              notes={notes.filter((n) => n.section === d.doc_type)}
              onChanged={load}
            />
          ))}
        </div>

        <div className="space-y-4">
          <SignatureOnFile driverId={id} legalName={driver.name} canUpdate={false} />
          <LicenseDetails prefill={license_prefill} />
          <RequestsPanel driverId={id} requests={requests} onChanged={load} docTypes={docTypeOptions} />
          <GeneralNotes driverId={id} notes={generalNotes} onChanged={load} />
        </div>
      </div>
    </div>
  );
}

function DocSection({ driverId, docType, label, status, versions, notes, onChanged }) {
  const { openPreview } = useFilePreview();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteDate, setNoteDate] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const pageInputRef = useRef(null);

  const latest = versions[0] || null;
  const meta = STATUS_META[status] || STATUS_META.missing;
  const Icon = meta.Icon;

  const upload = async () => {
    if (!file) return toast.error("Choose the file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      if (expiresAt) fd.append("expires_at", expiresAt);
      fd.append("file", file);
      await api.post(`/drivers/${driverId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setFile(null);
      setExpiresAt("");
      setUploadOpen(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const viewVersion = (v, page) => {
    const pageEntry = page ? v.pages.find((p) => p.n === page) : null;
    openPreview({
      kind: page ? "page" : "document",
      id: v.id,
      page,
      meta: {
        label: page ? `${label} · page ${page}` : label,
        doc_type: docType,
        status,
        expires_at: v.expires_at,
        uploaded_at: page ? pageEntry?.uploaded_at : v.uploaded_at,
        original_filename: page ? pageEntry?.original_filename : v.original_filename,
        content_type: page ? pageEntry?.content_type : v.content_type,
        driver_id: driverId,
        canEdit: !page,
        notes: v.notes,
        onChanged,
      },
    });
  };

  const addPage = async (f) => {
    if (!f || !latest) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/drivers/documents/${latest.id}/pages`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Page added");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return toast.error("Write the note first");
    setSavingNote(true);
    try {
      await api.post(`/drivers/${driverId}/file-notes`, { section: docType, note: text, remind_on: noteDate || null });
      setNoteText("");
      setNoteDate("");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingNote(false);
    }
  };

  const toggleNoteDone = async (n) => {
    try {
      await api.patch(`/drivers/file-notes/${n.id}`, { done: !n.done });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteNote = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/drivers/file-notes/${n.id}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-3" data-testid={`docsection-${docType}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-medium text-sm text-slate-900">{label}</div>
          <span className={`inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 border ${meta.cls}`}>
            <Icon size={10} /> {meta.label}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {latest && (
            <button onClick={() => viewVersion(latest)} className="text-[#005E82] hover:underline text-xs min-h-[44px] sm:min-h-0" data-testid={`view-${docType}`}>
              View
            </button>
          )}
          {latest && (
            <>
              <button
                onClick={() => pageInputRef.current?.click()}
                className="text-xs px-2 py-1.5 rounded-md border border-slate-200 min-h-[44px] sm:min-h-0 inline-flex items-center gap-1"
                data-testid={`addpage-${docType}`}
              >
                <FilePlus2 size={12} /> Add page
              </button>
              <input
                ref={pageInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) addPage(f);
                }}
              />
            </>
          )}
          <button
            onClick={() => setUploadOpen((v) => !v)}
            className="btn-primary text-[11px] px-2 py-1.5 flex items-center gap-1 min-h-[44px] sm:min-h-0"
            data-testid={`upload-${docType}`}
          >
            <Upload size={11} /> {latest ? "Replace" : "Upload"}
          </button>
        </div>
      </div>

      {latest && (
        <div className="text-xs text-slate-500 mt-1.5">
          <span className="font-mono">{latest.original_filename}</span>
          {latest.expires_at && <span> · expires {latest.expires_at}</span>}
          {latest.page_count > 0 && <span> · {latest.page_count + 1} pages</span>}
          <div className="text-slate-400 text-[11px] mt-0.5">Uploaded {new Date(latest.uploaded_at).toLocaleDateString()} by {latest.uploaded_by_name}</div>
        </div>
      )}

      {latest && latest.pages.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
          {latest.pages.map((p) => (
            <button key={p.n} onClick={() => viewVersion(latest, p.n)} className="text-[11px] text-[#005E82] hover:underline" data-testid={`view-page-${docType}-${p.n}`}>
              Page {p.n}
            </button>
          ))}
        </div>
      )}

      {uploadOpen && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-end gap-2">
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs"
            data-testid={`upload-file-${docType}`}
          />
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            title="Expiry date (if applicable)"
            className="text-sm px-2 py-2 rounded-md border border-slate-200 min-h-[44px]"
            data-testid={`upload-expiry-${docType}`}
          />
          <button onClick={upload} disabled={busy || !file} className="btn-primary text-xs px-3 min-h-[44px] disabled:opacity-60" data-testid={`upload-save-${docType}`}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Notes &amp; reminders</div>
        {notes.length === 0 && <div className="text-xs text-slate-400 mb-2">No notes yet.</div>}
        {notes.map((n) => (
          <div key={n.id} className={`text-xs border-b border-slate-50 py-1.5 flex items-center gap-2 ${n.done ? "opacity-50" : ""}`} data-testid={`note-${n.id}`}>
            <span className="flex-1">{n.note}</span>
            {n.remind_on && <span className="text-slate-400 whitespace-nowrap">remind {n.remind_on}</span>}
            <button onClick={() => toggleNoteDone(n)} className="text-slate-400 hover:text-emerald-600 min-h-[28px] min-w-[28px]" title={n.done ? "Mark not done" : "Mark done"}>
              <CheckCircle2 size={13} />
            </button>
            <button onClick={() => deleteNote(n)} className="text-slate-400 hover:text-rose-600 min-h-[28px] min-w-[28px]" title="Delete note">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <input
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note or reminder..."
            className="flex-1 min-w-[140px] text-xs px-2 py-2 rounded-md border border-slate-200 min-h-[40px]"
            data-testid={`note-input-${docType}`}
          />
          <input
            type="date"
            value={noteDate}
            onChange={(e) => setNoteDate(e.target.value)}
            title="Remind on (optional)"
            className="text-xs px-2 py-2 rounded-md border border-slate-200 min-h-[40px]"
          />
          <button onClick={addNote} disabled={savingNote} className="text-xs px-3 py-2 rounded-md border border-slate-200 min-h-[40px] disabled:opacity-60" data-testid={`note-add-${docType}`}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// "Onboarding N of M" pill, from GET /api/drivers/{did}/onboarding (contract I). A click
// expands an inline list of what is still missing, so the admin does not have to leave the
// Driver File to see why the count is short of the total.
function OnboardingPill({ onboarding }) {
  const [open, setOpen] = useState(false);
  const missing = onboarding.steps.filter((s) => !s.optional && !s.done);
  const complete = missing.length === 0;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center rounded-full px-2 py-0.5 border text-[10px] font-medium ${
          complete ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"
        }`}
        data-testid="onboarding-pill"
      >
        Onboarding {onboarding.completed} of {onboarding.total}
      </button>
      {open && (
        <div className="absolute z-10 mt-1.5 left-0 w-64 bg-white border border-slate-200 rounded-md shadow-lg p-3 text-xs" data-testid="onboarding-pill-missing">
          {complete ? (
            <div className="text-slate-500">Every required step is complete.</div>
          ) : (
            <>
              <div className="text-slate-500 font-semibold uppercase tracking-wider text-[10px] mb-1.5">Still missing</div>
              <ul className="space-y-1">
                {missing.map((s) => (
                  <li key={s.key} className="text-slate-700">{s.title}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-2 border-b border-slate-50 py-1">
      <span className="text-slate-400">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

function LicenseDetails({ prefill }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="license-details">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">License details</div>
      {!prefill && <div className="text-xs text-slate-400">No license read on file yet.</div>}
      {prefill && (
        <div className="text-xs text-slate-700">
          <Row label="Name" value={[prefill.legal_first, prefill.legal_middle, prefill.legal_last].filter(Boolean).join(" ")} />
          <Row label="DOB" value={prefill.dob} />
          <Row label="Address" value={[prefill.addr_street, prefill.addr_city, prefill.addr_state, prefill.addr_zip].filter(Boolean).join(", ")} />
          <Row label="License #" value={prefill.license_number} />
          <Row label="Class" value={prefill.license_class} />
          <Row label="State" value={prefill.license_state} />
          <Row label="Expires" value={prefill.license_expiry} />
          <Row label="Source" value={prefill.source} />
        </div>
      )}
    </div>
  );
}

function RequestsPanel({ driverId, requests, onChanged, docTypes }) {
  const [preset, setPreset] = useState(REQUEST_PRESETS[0]);
  const [title, setTitle] = useState(REQUEST_PRESETS[0]);
  const [note, setNote] = useState("");
  const [docType, setDocType] = useState("");
  const [busy, setBusy] = useState(false);

  const onPresetChange = (e) => {
    const v = e.target.value;
    setPreset(v);
    if (v !== "Other") setTitle(v);
  };

  const send = async () => {
    const t = title.trim();
    if (!t) return toast.error("Give the request a title");
    setBusy(true);
    try {
      await api.post(`/drivers/${driverId}/requests`, { title: t, note: note.trim() || null, doc_type: docType || null });
      toast.success("Request sent, the driver will see it in their portal");
      setNote("");
      setDocType("");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (rid, status) => {
    try {
      await api.patch(`/drivers/requests/${rid}`, { status });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="requests-panel">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Requests to the driver</div>
      <div className="space-y-2 mb-3">
        <Field label="Request" testId="request-preset" value={preset} onChange={onPresetChange} options={REQUEST_PRESETS.map((p) => ({ value: p, label: p }))} />
        <Field label="Title (what they'll see)" testId="request-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-200"
            placeholder="e.g. Concentra Eden Prairie, before Friday"
            data-testid="request-note"
          />
        </div>
        <Field
          label="Related document (optional)"
          testId="request-doctype"
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          options={[{ value: "", label: "None" }, ...docTypes.map((d) => ({ value: d.key, label: d.label }))]}
        />
        <button onClick={send} disabled={busy} className="btn-primary text-xs px-3 py-2.5 min-h-[44px] disabled:opacity-60 w-full" data-testid="send-request-btn">
          {busy ? "Sending…" : "Send request"}
        </button>
      </div>
      {requests.length === 0 && <div className="text-xs text-slate-400">No requests yet.</div>}
      {requests.map((r) => (
        <div key={r.id} className="border border-slate-200 rounded-md p-2.5 mb-2 text-xs" data-testid={`request-${r.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 border text-[10px] font-medium capitalize ${
                r.status === "open"
                  ? "text-amber-700 bg-amber-50 border-amber-200"
                  : r.status === "done"
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : "text-slate-500 bg-slate-50 border-slate-200"
              }`}
            >
              {r.status}
            </span>
            <span className="text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
          </div>
          <div className="mt-1 font-medium text-slate-800">{r.title}</div>
          {r.note && <div className="text-slate-500 mt-0.5">{r.note}</div>}
          {r.status === "open" && (
            <div className="flex gap-2 mt-2">
              <button onClick={() => setStatus(r.id, "done")} className="text-[11px] px-2 py-1.5 rounded-md border border-slate-200 min-h-[32px]" data-testid={`request-done-${r.id}`}>
                Mark done
              </button>
              <button onClick={() => setStatus(r.id, "cancelled")} className="text-[11px] px-2 py-1.5 rounded-md border border-rose-200 text-rose-600 min-h-[32px]" data-testid={`request-cancel-${r.id}`}>
                Cancel
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function GeneralNotes({ driverId, notes, onChanged }) {
  const [text, setText] = useState("");
  const [remindOn, setRemindOn] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const t = text.trim();
    if (!t) return toast.error("Write the note first");
    setBusy(true);
    try {
      await api.post(`/drivers/${driverId}/file-notes`, { section: "general", note: t, remind_on: remindOn || null });
      setText("");
      setRemindOn("");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleDone = async (n) => {
    try {
      await api.patch(`/drivers/file-notes/${n.id}`, { done: !n.done });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/drivers/file-notes/${n.id}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="general-notes">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
        General notes <span className="normal-case font-normal text-slate-400">internal only, the driver never sees these</span>
      </div>
      {notes.length === 0 && <div className="text-xs text-slate-400 mb-2">No notes yet.</div>}
      {notes.map((n) => (
        <div key={n.id} className={`text-xs border-b border-slate-50 py-1.5 flex items-center gap-2 ${n.done ? "opacity-50" : ""}`} data-testid={`gnote-${n.id}`}>
          <span className="flex-1">{n.note}</span>
          {n.remind_on && <span className="text-slate-400 whitespace-nowrap">remind {n.remind_on}</span>}
          <button onClick={() => toggleDone(n)} className="text-slate-400 hover:text-emerald-600 min-h-[28px] min-w-[28px]" title={n.done ? "Mark not done" : "Mark done"}>
            <CheckCircle2 size={13} />
          </button>
          <button onClick={() => del(n)} className="text-slate-400 hover:text-rose-600 min-h-[28px] min-w-[28px]" title="Delete note">
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note or reminder..."
          className="flex-1 min-w-[140px] text-xs px-2 py-2 rounded-md border border-slate-200 min-h-[40px]"
          data-testid="gnote-input"
        />
        <input
          type="date"
          value={remindOn}
          onChange={(e) => setRemindOn(e.target.value)}
          title="Remind on (optional)"
          className="text-xs px-2 py-2 rounded-md border border-slate-200 min-h-[40px]"
        />
        <button onClick={add} disabled={busy} className="text-xs px-3 py-2 rounded-md border border-slate-200 min-h-[40px] disabled:opacity-60" data-testid="gnote-add">
          Add
        </button>
      </div>
    </div>
  );
}
